import React, {memo} from 'react';
import {DialogTypes} from 'common/names';
import Dialog from 'components/Dialog';
import FormikBrowseFileInput from 'components/inputs/FormikBrowseFileInput';
import FormikTextField from 'components/inputs/FormikTextField';
import Panel from 'components/Panel';
import {useTranslation} from 'react-i18next';
import {usePostApi} from 'common/hooks/usePostApi';
import api from 'api';
import {useDispatch, useSelector} from 'react-redux';
import actions from 'features/layout/redux/actions';
import {ImportSSLCertificate} from 'types/ImportSSLCertificate';
import {ImportClientSSLCertificate} from 'types/ImportClientSSLCertificate';
import selectors from 'features/layout/redux/selectors';
import {AlertDialog} from 'components/dialogs/AlertDialog';
import Typography from '@mui/material/Typography';
import {useModal} from 'common/hooks/useModal';
import {INav} from 'features/layout/redux/helpers/FoldersHelper';
import Formik, {FormOnSubmitHelpers} from 'components/hook-form/Formik';
import {FormFooterButtons} from 'components/buttons/FormFooterButtons';
import {SslKeyNames} from 'types/diplomat/keys/SslNames';

type Props = {
   data?: INav;
   client?: boolean;
   onClose: () => void;
};
const ImportSSLCertificateDialog = memo(({data, client = false, onClose}: Props) => {
   const {t} = useTranslation();
   const {open, handleOpen, handleClose} = useModal();
   const keys = useSelector(selectors.clientSslKeys);
   const certRootFolderId = useSelector(selectors.sslCertRootFolder(client));
   const dispatch = useDispatch();
   const onSuccess = () => {
      dispatch(actions.loadTreeNav());
      onClose();
   };
   const {request, error} = usePostApi(client ? api.importClientSSL : api.importServerSSL);
   const dialogTitle = client
      ? DialogTypes.ImportSSLClientCertificate.dialogName
      : DialogTypes.ImportSSLServerCertificate.dialogName;
   const prefix = client ? t('Client SSL Certificate') : t('Server SSL Certificate');
   const filePathLabel = `${prefix} ${t('File')}`;
   const certNameLabel = `${prefix} ${t('Name')}`;
   const schema = client ? ImportClientSSLCertificate : ImportSSLCertificate;
   const filesOfType = client ? ['*.pfx', '*.p12', '*.*'] : ['*.crt', '*.*'];
   const validateCertName = (value: string) => {
      if (!client) {
         return;
      }
      const isNameExist = keys.some((key) => key.name === value);
      if (isNameExist) {
         return t('SSL Certificate with entered name already exist');
      }
   };
   const handleSubmit = (values: any, helpers: FormOnSubmitHelpers<any>) => {
      request(JSON.stringify(values), {onSuccess, onFailure: handleOpen, helpers});
   };
   return (
      <Dialog maxWidth="sm" header={dialogTitle} close={onClose}>
         {error ? (
            <AlertDialog
               open={open}
               title={t('Error Importing SSL Certificate')}
               content={<Typography>{error.errors?.[0].message}</Typography>}
               onClose={handleClose}
            />
         ) : null}
         <Formik
            initialValues={schema.cast({
               folderID: data?.id ?? certRootFolderId,
            })}
            onSubmit={handleSubmit}
            validationSchema={schema}
         >
            <>
               <Panel root>
                  <FormikBrowseFileInput
                     placeholder={filePathLabel}
                     name={SslKeyNames.filepath}
                     filesOfType={filesOfType}
                  />
                  {/* <FormikTextField
                     autoFocus
                     name={SslKeyNames.filepath}
                     label={t('Certificate Body')}
                     multiline
                     rows={8}
                  /> */}
                  <FormikTextField
                     label={certNameLabel}
                     name={SslKeyNames.certName}
                     validate={validateCertName}
                  />
                  {client ? (
                     <FormikTextField
                        label={t('Passphrase')}
                        type={'password'}
                        name={SslKeyNames.passphrase}
                     />
                  ) : null}
               </Panel>
               <FormFooterButtons cancel={onClose} />
            </>
         </Formik>
      </Dialog>
   );
});

export default ImportSSLCertificateDialog;
